import { useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from '@draftedge/ui';
import { useAuthStore } from '@/lib/auth';
import { queryClient } from '@/lib/queryClient';
import { isNativeApp } from '@/lib/platform';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Auth gate for routes that require a logged-in user.
 *
 * - While the initial /me lookup is in flight (`isBootstrapping`), shows a
 *   neutral loading state instead of flashing the login page.
 * - On unauthenticated, redirects to /login with the original location in
 *   `state.from` so we can return after sign-in.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const location = useLocation();

  if (isBootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-muted">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    // NATIVE: there is no login. Unauthenticated here means device
    // provisioning failed (bad network on first launch). Never show the
    // sign-in form - offer a retry that re-runs the silent activation.
    if (isNativeApp()) {
      return <NativeActivationRetry />;
    }
    // Preserve the FULL location (path + query + hash) so the user returns to
    // exactly where they were after signing in — not a stripped pathname.
    const from = location.pathname + location.search + location.hash;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  return <>{children}</>;
}

/**
 * Native-only stand-in for the login redirect: silent device activation
 * failed (usually no connectivity on first launch). Retry re-runs the
 * bootstrap; nothing here mentions accounts or sign-in.
 */
function NativeActivationRetry() {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-8 text-center">
      <p className="font-display text-lg font-bold text-ink">
        Finishing setup
      </p>
      <p className="text-sm text-ink-muted">
        DraftEdge could not reach the server to activate this device. Check
        your connection and try again.
      </p>
      <Button
        variant="primary"
        size="md"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          // Clear tier-gated caches after the retry: the anonymous
          // free-tier player pool has a multi-hour staleTime and would
          // otherwise survive into the now-licensed session (audit F1).
          void bootstrap()
            .then(() => queryClient.clear())
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'Retrying...' : 'Retry'}
      </Button>
    </div>
  );
}
